"use client";
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { appealBan, getUserBan } from "@/lib/api/ban";
import { useGlobalContext } from "@/app/context/store";
import { BanLevel } from "@/lib/constants/ban";

interface BanModalProps {
  isOpen: boolean;
  onClose: () => void;
  translations: any;
}

export default function BanModal({ isOpen, onClose, translations }: BanModalProps) {
  const { user } = useGlobalContext();
  const [appealReason, setAppealReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [appealSubmitted, setAppealSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  if (!isOpen) return null;

  // Map ban levels to user-friendly descriptions
  const BanLevelDescriptions = {
    [BanLevel.NO_SAVE]: translations.noSave,
    [BanLevel.NO_GENERATE]: translations.noGenerate,
    [BanLevel.SITE_WIDE]: translations.siteWide,
  };

  const handleSubmitAppeal = async (e: React.FormEvent) => {
    e.preventDefault();

    if (appealReason.trim().length < 10) {
      setErrorMessage(translations.appealSubmissionMinLength);
      return;
    }
    if (appealReason.trim().length > 500) {
      setErrorMessage(translations.appealSubmissionMaxLength);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const response = await appealBan(appealReason);

      if (response.status !== 200) {
        throw new Error("Failed to submit appeal");
      }

      setAppealSubmitted(true);
    } catch (error) {
      console.error("Error submitting appeal:", error);
      setErrorMessage(translations.appealFailure);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const appealAlreadySubmitted = () => {
    return user.banned?.currentBan?.appeal || false;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-gray-800">
        <div className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-red-400">{translations.accountRestricted}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <X size={24} />
            </button>
          </div>

          <div className="space-y-4">
            <div className="rounded-md border border-red-900/50 bg-red-900/20 p-4">
              <h3 className="mb-2 text-lg font-medium text-red-500">
                {" "}
                {translations.restrictionDetails}
              </h3>
              <p className="mb-2 text-red-500">
                <span className="text-gray-400">{translations.type}</span>{" "}
                {BanLevelDescriptions[user.banned?.level as BanLevel] ||
                  `Ban level ${user.banned?.level}`}
              </p>
              <p className="mb-2 text-red-500">
                <span className="text-gray-400">{translations.reason}</span>{" "}
                {user.banned?.currentBan.reason}
              </p>
              <p className="text-red-500">
                <span className="text-gray-400">{translations.expires}</span>{" "}
                {formatDate(user.banned?.pardonDate.toString() || new Date().toString())}
              </p>
            </div>

            {appealAlreadySubmitted() ? (
              <div className="rounded-md border border-yellow-900/50 bg-yellow-900/20 p-4">
                <p className="text-yellow-300">{translations.appealSubmitted}</p>
                {user.banned?.currentBan?.appeal?.appealReason && (
                  <div className="mt-3">
                    <p className="mb-2 font-medium text-gray-300">{translations.appealMessage}</p>
                    <div className="rounded-md bg-gray-700/50 p-3 text-gray-200">
                      {user.banned?.currentBan?.appeal?.appealReason}
                    </div>
                  </div>
                )}
              </div>
            ) : appealSubmitted ? (
              <div className="rounded-md border border-green-900/50 bg-green-900/20 p-4">
                <p className="text-green-300">{translations.appealSuccess}</p>
                <p className="mt-2 text-gray-400">{translations.appealSubmitted}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmitAppeal} className="space-y-4">
                <div>
                  <label className="mb-2 block font-medium text-gray-300">
                    {translations.appealText}
                  </label>
                  <textarea
                    className="h-32 w-full rounded-md border border-gray-600 bg-gray-700 p-3 text-white"
                    placeholder={translations.appealSubmissionPlaceholder}
                    value={appealReason}
                    onChange={(e) => setAppealReason(e.target.value)}
                    disabled={isSubmitting}
                  ></textarea>
                  {errorMessage && <p className="mt-1 text-sm text-red-400">{errorMessage}</p>}
                </div>
                <button
                  type="submit"
                  className="w-full rounded-md bg-blue-600 py-2 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? translations.appealSubmissionButtonLoading
                    : translations.appealSubmissionButton}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
